/**
 * OfflineProvider — top-level provider that:
 *   1. Boots the sync engine with the current (companyId, businessId)
 *   2. Listens to online state and tab focus
 *   3. Exposes a `useOffline()` hook with helpers
 *
 * Mount once near the app root, INSIDE auth + business providers so we
 * know which user + business the queue belongs to.
 *
 *   <AuthProvider>
 *     <BusinessProvider>
 *       <OfflineProvider api={api}>
 *         <App />
 *       </OfflineProvider>
 *     </BusinessProvider>
 *   </AuthProvider>
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AxiosInstance } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { SyncEngine } from "@/offline/sync-engine";
import { enqueue as enqueueOutbox, type EnqueueInput } from "@/offline/outbox";
import { useOnlineStatus, type OnlineStatus } from "@/hooks/useOnlineStatus";
import useAuthStore from "@/stores/authStore";
import useBusinessStore from "@/stores/businessStore";

// ── Context shape ───────────────────────────────────────────────

interface OfflineContextValue {
  /** Combined online flag from useOnlineStatus. */
  status: OnlineStatus;
  /** Enqueue a server action. Returns the outbox item id (= idempotencyKey). */
  enqueue: (
    input: Omit<EnqueueInput, "companyId" | "businessId" | "userId">,
  ) => Promise<string>;
  /** Force an immediate sync attempt. */
  syncNow: () => Promise<void>;
  /** True if this tab is the active sync leader. */
  isLeader: boolean;
  /** True when the engine has booted (companyId + businessId are set). */
  ready: boolean;
  /** Whose queue this is — the bar and the till read the same scope. */
  scope: { companyId: string | null; businessId: string | null; userId: string | null };
}

const OfflineContext = createContext<OfflineContextValue | null>(null);

// ── Provider ────────────────────────────────────────────────────

interface OfflineProviderProps {
  api: AxiosInstance;
  /** Optional heartbeat URL override. */
  heartbeatUrl?: string | null;
  children: React.ReactNode;
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({
  api,
  heartbeatUrl = "/health",
  children,
}) => {
  // Pull scope from auth + business stores
  const user = useAuthStore((s) => s.user);
  const businesses = useBusinessStore((s) => s.availableBusinesses);
  const activeSlug = useBusinessStore((s) => s.activeBusinessSlug);
  const activeBusiness = useMemo(
    () => businesses.find((b) => b.slug === activeSlug) || null,
    [activeSlug, businesses],
  );

  const activeBusinessId = useBusinessStore((s) => s.activeBusinessId);
  const companyId = user?.companyId || null;
  // A cashier may not have the business list loaded — fall back to the
  // persisted active id, then the cashier's own business.
  const businessId =
    activeBusiness?._id || activeBusinessId || (user as { businessId?: string } | null)?.businessId || null;
  const userId = user?.id ?? null;
  const qc = useQueryClient();

  // The heartbeat must hit the API, not this site (a relative "/health"
  // would be answered by the web host's index.html and always look "up").
  const base = String(api.defaults.baseURL || "").replace(/\/+$/, "");
  const hb = heartbeatUrl && !/^https?:/i.test(heartbeatUrl) && base ? `${base}${heartbeatUrl}` : heartbeatUrl;
  const status = useOnlineStatus({ heartbeatUrl: hb, intervalMs: 20_000 });
  const [isLeader, setIsLeader] = useState(false);
  const [ready, setReady] = useState(false);

  // ── Boot the engine when scope changes ────────────────────
  useEffect(() => {
    if (!companyId || !businessId) {
      SyncEngine.stop();
      setReady(false);
      return;
    }
    SyncEngine.start({
      api,
      companyId,
      businessId,
      userId,
      onTick: () => setIsLeader(SyncEngine.isLeader()),
      onSynced: (item) => {
        if (item.type === "order.create") {
          // The sale is on the server now: refresh sales, stock and totals.
          for (const key of [["orders"], ["cashier-sales"], ["cashier", "products"], ["products"], ["reports"], ["shifts"]]) {
            qc.invalidateQueries({ queryKey: key });
          }
        }
      },
    });
    setReady(true);
    return () => {
      SyncEngine.stop();
      setReady(false);
    };
  }, [api, companyId, businessId, userId, qc]);

  // ── Drain on focus (catch up after tab was backgrounded) ──
  useEffect(() => {
    const onFocus = () => SyncEngine.drainNow().catch(() => {});
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
      document.addEventListener("visibilitychange", onFocus);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
        document.removeEventListener("visibilitychange", onFocus);
      }
    };
  }, []);

  // ── Scoped enqueue helper ──────────────────────────────────
  const enqueue = useCallback(
    async (
      input: Omit<EnqueueInput, "companyId" | "businessId" | "userId">,
    ): Promise<string> => {
      if (!companyId || !businessId) {
        throw new Error(
          "OfflineProvider: cannot enqueue without an active business",
        );
      }
      const item = await enqueueOutbox({
        ...input,
        companyId,
        businessId,
        userId,
      });
      return item.id;
    },
    [companyId, businessId, userId],
  );

  const syncNow = useCallback(async () => {
    await SyncEngine.drainNow();
  }, []);

  const value: OfflineContextValue = useMemo(
    () => ({ status, enqueue, syncNow, isLeader, ready, scope: { companyId, businessId, userId } }),
    [status, enqueue, syncNow, isLeader, ready, companyId, businessId, userId],
  );

  return (
    <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
  );
};

// ── Hook ────────────────────────────────────────────────────────

export function useOffline(): OfflineContextValue {
  const ctx = useContext(OfflineContext);
  if (!ctx) {
    throw new Error(
      "useOffline() must be called inside <OfflineProvider>. Wrap App.tsx with it.",
    );
  }
  return ctx;
}

/** Safe variant that returns null when no provider is mounted yet. */
export function useOfflineOptional(): OfflineContextValue | null {
  return useContext(OfflineContext);
}
